if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          galleryDots: this.querySelector('.gallery-dots'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        this._boundOnSlideChanged = debounce(this.onSlideChanged.bind(this), 500);
        this.elements.viewer.addEventListener('slideChanged', this._boundOnSlideChanged);
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
        if (this.elements.galleryDots) {
          this.elements.galleryDots.querySelectorAll('.gallery-dots__dot').forEach((dot) => {
            dot.addEventListener('click', () => this.setActiveMedia(dot.dataset.target, false));
          });
        }
        this.bindViewerArrows();
        this.bindDesktopScrollSync();
      }

      bindDesktopScrollSync() {
        if (!this.mql.matches || !this.elements.galleryDots) return;
        const list = this.elements.viewer.querySelector('[id^="Slider-"]') || this.elements.viewer.querySelector('.product__media-list');
        if (!list) return;
        let rafId = null;
        list.addEventListener('scroll', () => {
          if (rafId !== null) return;
          rafId = requestAnimationFrame(() => {
            rafId = null;
            this._syncIndicatorFromScroll();
          });
        }, { passive: true });
      }

      _syncIndicatorFromScroll() {
        if (!this.mql.matches || this._programmaticScroll) return;
        const list = this.elements.viewer.querySelector('[id^="Slider-"]') || this.elements.viewer.querySelector('.product__media-list');
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        if (!list || !slides.length) return;
        const slideWidth = list.clientWidth;
        if (slideWidth <= 0) return;
        const index = Math.min(slides.length - 1, Math.max(0, Math.round(list.scrollLeft / slideWidth)));
        const slide = slides[index];
        const mediaId = slide?.dataset?.mediaId;
        if (!mediaId || this._currentSlideIndex === index) return;
        this._currentSlideIndex = index;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        slide.classList.add('is-active');
        this.setActiveDotByIndex(index);
        this.playActiveMedia(slide);
      }

      bindViewerArrows() {
        const prevBtn = this.elements.viewer?.querySelector('button[name="previous"]');
        const nextBtn = this.elements.viewer?.querySelector('button[name="next"]');
        if (!prevBtn || !nextBtn) return;
        const handleArrow = (e, direction) => {
          if (!this.mql.matches) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.goToAdjacentMedia(direction);
        };
        prevBtn.addEventListener('click', (e) => handleArrow(e, -1), true);
        nextBtn.addEventListener('click', (e) => handleArrow(e, 1), true);
      }

      goToAdjacentMedia(direction) {
        if (!this.mql.matches) return;
        const list = this.elements.viewer.querySelector('[id^="Slider-"]') || this.elements.viewer.querySelector('.product__media-list');
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        if (!list || slides.length < 2) return;
        // Simulate a swipe: scroll by one slide width. The same scroll handler that runs on swipe will update the indicator.
        const step = list.clientWidth * direction;
        list.scrollBy({ left: step, behavior: 'auto' });
      }

      _readActiveSlideIndex(slides) {
        const active = this.elements.viewer.querySelector('.is-active[data-media-id]') || slides[0];
        return Math.max(0, Array.from(slides).indexOf(active));
      }

      onSlideChanged(event) {
        // On desktop we control slides/dots via arrows and dot clicks only; ignore slider's scroll-driven events so they can't overwrite.
        if (this.mql.matches) return;
        const currentElement = event.detail?.currentElement;
        if (!currentElement?.dataset?.mediaId) return;
        const mediaId = currentElement.dataset.mediaId;
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        this.setActiveDot(mediaId);
      }

      setActiveMedia(mediaId, prepend, activeSlideElement, skipScroll) {
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        const activeMedia =
          activeSlideElement ||
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const effectiveMediaId = activeMedia.dataset.mediaId || mediaId;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        this._currentSlideIndex = Array.from(slides).indexOf(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${effectiveMediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Force reflow so the new .is-active (and display:block) is applied before anything else runs.
        void activeMedia.offsetHeight;

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        // Update dots by index (skip when arrow will update after scroll so indicator and image stay in sync).
        if (!skipScroll) {
          this.setActiveDotByIndex(this._currentSlideIndex);
        }
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${effectiveMediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        // Scroll to show the active slide (skip when arrow uses scrollBy in goToAdjacentMedia).
        if (!skipScroll) {
          const scrollParent = activeMedia.parentElement;
          const targetScrollLeft = activeMedia.offsetLeft;
          if (scrollParent) {
            this._programmaticScroll = true;
            scrollParent.scrollTo({ left: targetScrollLeft, behavior: 'auto' });
            setTimeout(() => {
              if (scrollParent.scrollLeft !== targetScrollLeft) {
                scrollParent.scrollTo({ left: targetScrollLeft, behavior: 'auto' });
              }
              setTimeout(() => { this._programmaticScroll = false; }, 150);
            }, 0);
          }
        }
        const activeMediaRect = activeMedia.getBoundingClientRect();
        if (activeMediaRect.top < -0.5) {
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      }

      setActiveDot(mediaId) {
        if (!this.elements.galleryDots || !mediaId) return;
        this.elements.galleryDots.querySelectorAll('.gallery-dots__dot').forEach((dot) => {
          dot.setAttribute('aria-current', dot.dataset.target === mediaId ? 'true' : 'false');
        });
      }

      setActiveDotByIndex(index) {
        if (!this.elements.galleryDots) return;
        const dots = this.elements.galleryDots.querySelectorAll('.gallery-dots__dot');
        const i = Math.max(0, Math.min(index, dots.length - 1));
        dots.forEach((dot, idx) => {
          dot.setAttribute('aria-current', idx === i ? 'true' : 'false');
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
